import React from 'react'
import styles from './TableRenderer.module.css'

interface WithChildren {
  children: React.ReactNode
}

const isValidElement = (element: unknown): element is React.ReactElement<WithChildren> => {
  if (!React.isValidElement(element)) return false
  const props = element.props as { [key: string]: unknown }
  return 'children' in props
}

const TableRenderer: React.FC<WithChildren> = ({ children }) => {
  const rows = React.Children.toArray(children)
  const headerRows = rows.filter(
    (row) =>
      isValidElement(row) &&
      React.Children.toArray(row.props.children).some((cell) => React.isValidElement(cell) && cell.type === 'th'),
  ) as React.ReactElement<WithChildren>[]

  const bodyRows = rows.filter(
    (row) =>
      isValidElement(row) &&
      React.Children.toArray(row.props.children).every((cell) => React.isValidElement(cell) && cell.type === 'td'),
  ) as React.ReactElement<WithChildren>[]

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        {headerRows.length > 0 && (
          <thead>
            {headerRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {React.Children.map(row.props.children, (cell) => {
                  if (!isValidElement(cell)) return null
                  return (
                    <th key={cell.key} className={styles.th}>
                      {cell.props.children}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
        )}
        <tbody>
          {bodyRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {React.Children.map(row.props.children, (cell) => {
                if (!isValidElement(cell)) return null
                return (
                  <td key={cell.key} className={styles.td}>
                    {cell.props.children}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default TableRenderer
